import React, { useState, useEffect } from 'react';
import { supabase } from '../../../api/supabase';
import { CheckCircle2, Circle, Calendar, Anchor } from 'lucide-react';
import { message, Spin, Typography } from 'antd';
import { colors, spacing, radius, typography, transitions } from '../../../styles/monochromeTheme';

const { Title, Text } = Typography;

const ClosingChecklist = () => {
  const [tasks, setTasks] = useState([
    { id: 1, label: 'Schedule + passeports à Georges', icon: '📋' },
    { id: 2, label: 'Envoyer passeports à Mélissa + Gendarmes', icon: '📨' },
    { id: 3, label: 'Planning + ajouter les off/astreintes', icon: '📅' },
    { id: 4, label: 'Places à quai', icon: '⚓' },
    { id: 5, label: 'Envoyer mail surclassement/confirmation', icon: '✉️' },
    { id: 6, label: 'Imprimer les contrats bateaux et edge crew', icon: '🖨️' },
    { id: 7, label: 'Feuille de caisse', icon: '💰' },
    { id: 8, label: 'Vider la poubelle', icon: '🗑️' },
    { id: 9, label: 'Fermer la boutique', icon: '🔒' },
    { id: 10, label: 'Eteindre clim + brancher souris + fermer bureau', icon: '🏠' }
  ].map(t => ({ ...t, completed: false })));

  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState('');

  const getTodayDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    const options = { timeZone: 'America/Martinique', year: 'numeric', month: '2-digit', day: '2-digit' };
    const dateString = today.toLocaleDateString('en-CA', options);
    return dateString;
  };

  useEffect(() => {
    loadTodayTasks();
  }, []);

  const loadTodayTasks = async () => {
    try {
      const today = getTodayDate();
      setCurrentDate(today);

      const { data, error } = await supabase
        .from('closing_checklist')
        .select('*')
        .eq('date', today)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading tasks:', error);
        setLoading(false);
        return;
      }

      if (data) {
        setTasks(prevTasks =>
          prevTasks.map(task => ({
            ...task,
            completed: data.tasks[task.id] || false
          }))
        );
      }

      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      message.error('Erreur lors du chargement de la checklist');
      setLoading(false);
    }
  };

  const saveTasksToDatabase = async (updatedTasks) => {
    try {
      const today = getTodayDate();
      const tasksObject = {};

      updatedTasks.forEach(task => {
        tasksObject[task.id] = task.completed;
      });

      const { error } = await supabase
        .from('closing_checklist')
        .upsert({
          date: today,
          tasks: tasksObject,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'date'
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving tasks:', err);
      message.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleTask = async (taskId) => {
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );

    setTasks(updatedTasks);
    await saveTasksToDatabase(updatedTasks);
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const completionPercentage = Math.round((completedCount / tasks.length) * 100);

  // Find the current position for the boat (last completed task index)
  const getBoatPosition = () => {
    let lastCompleted = -1;
    for (let i = 0; i < tasks.length; i++) {
      if (tasks[i].completed) lastCompleted = i;
    }
    return lastCompleted;
  };

  const boatPosition = getBoatPosition();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${colors.bgPrimary} 0%, #f0f4f8 100%)`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${colors.bgPrimary} 0%, #f0f4f8 100%)`,
      padding: spacing.xxl,
    }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.xxl,
          padding: spacing.xl,
          background: colors.bgSecondary,
          borderRadius: radius.xxxl,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: radius.xxl,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              <Anchor size={32} color="#fff" strokeWidth={2} />
            </div>
            <div>
              <Title level={2} style={{ margin: 0, color: colors.textPrimary, letterSpacing: '-0.5px' }}>
                Checklist de Fermeture
              </Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs }}>
                <Calendar size={14} style={{ color: colors.textTertiary }} />
                <Text style={{ fontSize: typography.fontSize.sm, color: colors.textTertiary }}>
                  {new Date(currentDate).toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                  })}
                </Text>
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.md,
          }}>
            <div style={{
              padding: `${spacing.md} ${spacing.xl}`,
              background: '#1a1a2e',
              borderRadius: radius.xl,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              <span style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#ffffff',
              }}>
                {completedCount}/{tasks.length}
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar with Yacht */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: radius.xxxl,
          padding: spacing.xxl,
          marginBottom: spacing.xxl,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Water wave background */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(180deg, rgba(59,130,246,0.03) 0%, rgba(59,130,246,0.08) 100%)',
            borderRadius: `0 0 ${radius.xxxl} ${radius.xxxl}`,
          }} />

          <div style={{
            position: 'relative',
            height: '80px',
            marginBottom: spacing.lg,
          }}>
            {/* Track background with gradient */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '30px',
              right: '30px',
              height: '12px',
              background: 'linear-gradient(90deg, #e8eef5 0%, #dce4ed 100%)',
              borderRadius: '6px',
              transform: 'translateY(-50%)',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
            }} />

            {/* Filled track */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '30px',
              width: completedCount === 0 ? '0%' : `calc(${(completedCount / tasks.length) * 100}% - 60px * ${1 - completedCount / tasks.length})`,
              height: '12px',
              background: completionPercentage === 100
                ? 'linear-gradient(90deg, #52c41a 0%, #73d13d 100%)'
                : 'linear-gradient(90deg, #1a1a2e 0%, #2d3a4f 100%)',
              borderRadius: '6px',
              transform: 'translateY(-50%)',
              transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }} />

            {/* Station dots */}
            {tasks.map((task, index) => {
              const leftPercent = (index / (tasks.length - 1)) * 100;
              return (
                <div
                  key={task.id}
                  style={{
                    position: 'absolute',
                    left: `calc(${leftPercent}% - ${leftPercent * 0.6}px + 30px)`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: task.completed ? '20px' : '14px',
                    height: task.completed ? '20px' : '14px',
                    borderRadius: '50%',
                    background: task.completed ? '#52c41a' : '#fff',
                    border: `3px solid ${task.completed ? '#52c41a' : '#bdc3c7'}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 2,
                    boxShadow: task.completed
                      ? '0 2px 8px rgba(82, 196, 26, 0.4)'
                      : '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                />
              );
            })}

            {/* Yacht */}
            <div style={{
              position: 'absolute',
              left: boatPosition >= 0
                ? `calc(${(boatPosition / (tasks.length - 1)) * 100}% - ${(boatPosition / (tasks.length - 1)) * 100 * 0.6}px + 30px)`
                : '30px',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              transition: 'left 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: completionPercentage === 100
                  ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                  : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: completionPercentage === 100
                  ? '0 4px 16px rgba(82, 196, 26, 0.4)'
                  : '0 4px 16px rgba(0,0,0,0.2)',
                border: '3px solid #fff',
              }}>
                <span style={{ fontSize: '28px' }}>
                  {completionPercentage === 100 ? '🎉' : '🛥️'}
                </span>
              </div>
            </div>
          </div>

          {/* Percentage */}
          <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Text style={{
              fontSize: '42px',
              fontWeight: typography.fontWeight.bold,
              color: completionPercentage === 100 ? '#52c41a' : colors.textPrimary,
              letterSpacing: '-1px',
            }}>
              {completionPercentage}%
            </Text>
            <Text style={{
              display: 'block',
              fontSize: typography.fontSize.md,
              color: colors.textTertiary,
              marginTop: spacing.xs,
            }}>
              {completionPercentage === 100 ? 'Terminé ! Bonne soirée 🌙' : 'du parcours complété'}
            </Text>
          </div>
        </div>

        {/* Task Circuit */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: spacing.lg,
        }}>
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => toggleTask(task.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.lg,
                padding: spacing.xl,
                background: task.completed
                  ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                  : colors.bgSecondary,
                border: `2px solid ${task.completed ? '#52c41a' : colors.borderLight}`,
                borderRadius: radius.xxl,
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: task.completed
                  ? '0 4px 16px rgba(82, 196, 26, 0.15)'
                  : '0 2px 8px rgba(0,0,0,0.03)',
              }}
              onMouseEnter={(e) => {
                if (!task.completed) {
                  e.currentTarget.style.borderColor = colors.borderMedium;
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }
              }}
              onMouseLeave={(e) => {
                if (!task.completed) {
                  e.currentTarget.style.borderColor = colors.borderLight;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)';
                }
              }}
            >
              {/* Step number badge */}
              <div style={{
                position: 'absolute',
                top: spacing.md,
                right: spacing.md,
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: task.completed
                  ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                  : colors.bgTertiary,
                color: task.completed ? '#fff' : colors.textTertiary,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: task.completed ? '0 2px 6px rgba(82, 196, 26, 0.3)' : 'none',
              }}>
                {index + 1}
              </div>

              {/* Icon */}
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: radius.xl,
                background: task.completed
                  ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                  : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '26px',
                flexShrink: 0,
                boxShadow: task.completed
                  ? '0 4px 12px rgba(82, 196, 26, 0.25)'
                  : '0 2px 4px rgba(0,0,0,0.05)',
              }}>
                {task.completed ? (
                  <CheckCircle2 size={30} color="#fff" strokeWidth={2.5} />
                ) : (
                  task.icon
                )}
              </div>

              {/* Label */}
              <div style={{ flex: 1, paddingRight: spacing.xxl }}>
                <Text style={{
                  fontSize: typography.fontSize.md,
                  fontWeight: typography.fontWeight.semibold,
                  color: task.completed ? '#389e0d' : colors.textPrimary,
                  textDecoration: task.completed ? 'line-through' : 'none',
                  display: 'block',
                  lineHeight: 1.5,
                }}>
                  {task.label}
                </Text>
              </div>

              {/* Check indicator */}
              <div style={{ flexShrink: 0 }}>
                {task.completed ? (
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
                  }}>
                    <CheckCircle2 size={22} color="#fff" strokeWidth={3} />
                  </div>
                ) : (
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: `2px solid ${colors.borderMedium}`,
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Circle size={18} color={colors.textTertiary} strokeWidth={2} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Completion Message */}
        {completionPercentage === 100 && (
          <div style={{
            marginTop: spacing.xxl,
            padding: spacing.xxxl,
            background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 50%, #b7eb8f 100%)',
            borderRadius: radius.xxxl,
            border: '2px solid #52c41a',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(82, 196, 26, 0.2)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '120px',
              height: '120px',
              background: 'rgba(82, 196, 26, 0.1)',
              borderRadius: '50%',
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'rgba(82, 196, 26, 0.08)',
              borderRadius: '50%',
            }} />
            <div style={{ fontSize: '56px', marginBottom: spacing.lg, position: 'relative' }}>
              🎊
            </div>
            <Title level={2} style={{ margin: 0, color: '#389e0d', position: 'relative' }}>
              Bravo ! Toutes les tâches sont complétées
            </Title>
            <Text style={{
              display: 'block',
              marginTop: spacing.md,
              fontSize: typography.fontSize.lg,
              color: '#52c41a',
              position: 'relative',
            }}>
              Bonne soirée et à demain !
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClosingChecklist;
